import React, { useEffect, useState } from 'react';
import '../assets/styles/LandingPage.css';
import heroIllustration from '../assets/images/hero_illustration.png';
import logoImage from '../assets/images/dadbucks_logo_1.png';

const LandingPage = ({ onLogin, onSignup, onKidLogin, onBlog, onPrivacy }) => {
    // Interactive Runway Calculator State
    const [goalCost, setGoalCost] = useState(25);
    const [allowance, setAllowance] = useState(5);
    const [choreValue, setChoreValue] = useState(2.00);
    const [weeklyChores, setWeeklyChores] = useState(4);

    // Calculate runway metrics
    const weeklyEarnings = allowance + (choreValue * weeklyChores);
    const weeksToGoal = Math.ceil(goalCost / (weeklyEarnings || 1));
    const totalChores = weeksToGoal * weeklyChores;

    useEffect(() => {
        // SEO: Set dynamic page title and meta description
        document.title = "DadBucks - Automated Chores, Allowance & Financial Literacy for Kids";
        
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', 'Teach financial literacy and build accountability with DadBucks. Automate allowances, track chores with photo proof, set goal runways, and sync progress offline. Start for free!');

        // Scroll to top on load
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="landing-page">
            {/* Glassmorphic Navbar */}
            <nav className="navbar" id="main-nav">
                <div 
                    className="logo" 
                    id="nav-logo" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <img
                        src={logoImage}
                        alt="DadBucks Premium Logo"
                        className="logo-img"
                    />
                    <span className="logo-text">DadBucks</span>
                </div>
                <div className="nav-links">
                    <button className="btn-nav" id="nav-btn-blog" onClick={onBlog}>Blog</button>
                    <button className="btn-nav" id="nav-btn-kid-login" onClick={onKidLogin}>Kids Login</button>
                    <button className="btn-nav" id="nav-btn-parent-login" onClick={onLogin}>Parent Login</button>
                    <button className="btn-primary" id="nav-btn-signup" onClick={onSignup}>Sign Up</button>
                </div>
            </nav>

            <main>
                {/* Hero Section */}
                <header className="hero" id="hero-section">
                    <div className="hero-content">
                        <div className="badge" id="hero-badge">✨ Automating Family Economies</div>
                        <h1>The Family Ledger <br /><span className="gradient-text">Simplified.</span></h1>
                        <p className="hero-subtitle">
                            Teach kids financial literacy through accountability. Track chores with photo proof, automate allowances, and sync progress seamlessly—even offline.
                        </p>
                        <div className="cta-group">
                            <button className="btn-primary btn-large" id="hero-cta-signup" onClick={onSignup}>Start Your Journey Free</button>
                            <a href="#download" className="btn-secondary btn-large" id="hero-cta-download">Download App</a>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <img src={heroIllustration} alt="Happy family building habits and saving with DadBucks app" className="hero-img" />
                    </div>
                </header>

                {/* Stats Section */}
                <section className="stats-section" id="stats-section">
                    <div className="stat-item" id="stat-chores">
                        <span className="stat-number">150,000+</span>
                        <span className="stat-label">Chores Verified</span>
                    </div>
                    <div className="stat-item" id="stat-payouts">
                        <span className="stat-number">$1.2M+</span>
                        <span className="stat-label">Allowance Distributed</span>
                    </div>
                    <div className="stat-item" id="stat-rating">
                        <span className="stat-number">4.9 ★</span>
                        <span className="stat-label">App Store Rating</span>
                    </div>
                </section>

                {/* Core Features & Implemented Capabilities */}
                <section className="features-section" id="features-section">
                    <div className="section-header">
                        <h2>Inside the DadBucks App 📱</h2>
                        <p>Explore the powerful, fully-implemented features designed to run your household like a breeze.</p>
                    </div>

                    <div className="features-grid">
                        {/* Feature 1 */}
                        <article className="feature-card" id="feat-allowance">
                            <div className="feature-icon-wrapper gradient-indigo">
                                <span className="feature-icon" role="img" aria-label="money">💵</span>
                            </div>
                            <h3>Automated Allowance Engine</h3>
                            <span className="platform-badge mobile-web">Web & Mobile</span>
                            <p>
                                Define weekly allowance rates once. Set automatic distributions, manage manual bonus rewards for outstanding achievements, and track it all via a central family ledger.
                            </p>
                        </article>

                        {/* Feature 2 */}
                        <article className="feature-card" id="feat-chores">
                            <div className="feature-icon-wrapper gradient-emerald">
                                <span className="feature-icon" role="img" aria-label="checklist">📸</span>
                            </div>
                            <h3>Smart Chore Verification</h3>
                            <span className="platform-badge mobile-web">Web & Mobile</span>
                            <p>
                                Kids submit chore completion requests. Real-time media upload support allows kids to submit photographic evidence of their work so parents can approve with a single tap.
                            </p>
                        </article>

                        {/* Feature 3 */}
                        <article className="feature-card" id="feat-offline">
                            <div className="feature-icon-wrapper gradient-cyan">
                                <span className="feature-icon" role="img" aria-label="refresh">🔄</span>
                            </div>
                            <h3>Seamless Offline Sync</h3>
                            <span className="platform-badge mobile-only">Mobile App Only</span>
                            <p>
                                No connection? No problem. Kids can mark chores done while playing in the yard or driving. The app queues submissions locally and syncs automatically when online.
                            </p>
                        </article>

                        {/* Feature 4 */}
                        <article className="feature-card" id="feat-goals">
                            <div className="feature-icon-wrapper gradient-amber">
                                <span className="feature-icon" role="img" aria-label="rocket">🚀</span>
                            </div>
                            <h3>Goal Runway & Store</h3>
                            <span className="platform-badge mobile-web">Web & Mobile</span>
                            <p>
                                Kids select parent-approved goals (like a LEGO set or Roblox card). A visual "Runway progress bar" tells them exactly how many chores stand between them and their reward.
                            </p>
                        </article>

                        {/* Feature 5 */}
                        <article className="feature-card" id="feat-gamification">
                            <div className="feature-icon-wrapper gradient-violet">
                                <span className="feature-icon" role="img" aria-label="trophy">🏆</span>
                            </div>
                            <h3>Gamified Household Quests</h3>
                            <span className="platform-badge mobile-web">Web & Mobile</span>
                            <p>
                                Keep enthusiasm high with chore streaks, surprise bonus rewards, and leaderboard rankings. Completing a chore triggers celebratory animations and haptic feedback.
                            </p>
                        </article>

                        {/* Feature 6 */}
                        <article className="feature-card" id="feat-pinlock">
                            <div className="feature-icon-wrapper gradient-rose">
                                <span className="feature-icon" role="img" aria-label="lock">🔒</span>
                            </div>
                            <h3>Parent PIN Lock Security</h3>
                            <span className="platform-badge mobile-only">Mobile App Only</span>
                            <p>
                                Prevent unauthorized chore approvals or ledger modifications. Secure 4-digit PIN locks guard critical settings and payout buttons on shared household devices.
                            </p>
                        </article>
                    </div>
                </section>

                {/* Interactive Goal Runway Calculator */}
                <section className="interactive-section" id="runway-calculator-section">
                    <div className="calculator-container">
                        <div className="calculator-header">
                            <span className="badge">Try the Interactive Dashboard</span>
                            <h2>Calculate Your Kid's Goal Runway ✈️</h2>
                            <p>See how setting allowances and chore values translates to a visual timeline for their goals.</p>
                        </div>
                        
                        <div className="calculator-grid">
                            <div className="calculator-controls">
                                <div className="control-group">
                                    <label htmlFor="goal-cost-input">Goal Cost (e.g. LEGO Set Price): <strong>${goalCost}</strong></label>
                                    <input 
                                        type="range" 
                                        id="goal-cost-input" 
                                        min="10" 
                                        max="200" 
                                        step="5" 
                                        value={goalCost} 
                                        onChange={(e) => setGoalCost(Number(e.target.value))} 
                                    />
                                </div>
                                <div className="control-group">
                                    <label htmlFor="allowance-input">Weekly Base Allowance: <strong>${allowance}</strong></label>
                                    <input 
                                        type="range" 
                                        id="allowance-input" 
                                        min="0" 
                                        max="20" 
                                        step="1" 
                                        value={allowance} 
                                        onChange={(e) => setAllowance(Number(e.target.value))} 
                                    />
                                </div>
                                <div className="control-group">
                                    <label htmlFor="chore-val-input">Average Chore Payout: <strong>${choreValue.toFixed(2)}</strong></label>
                                    <input 
                                        type="range" 
                                        id="chore-val-input" 
                                        min="0.50" 
                                        max="5.00" 
                                        step="0.25" 
                                        value={choreValue} 
                                        onChange={(e) => setChoreValue(Number(e.target.value))} 
                                    />
                                </div>
                                <div className="control-group">
                                    <label htmlFor="weekly-chores-input">Chores Completed Weekly: <strong>{weeklyChores}</strong></label>
                                    <input 
                                        type="range" 
                                        id="weekly-chores-input" 
                                        min="1" 
                                        max="10" 
                                        step="1" 
                                        value={weeklyChores} 
                                        onChange={(e) => setWeeklyChores(Number(e.target.value))} 
                                    />
                                </div>
                            </div>

                            <div className="calculator-results">
                                <div className="results-card">
                                    <h4>Goal Runway Summary</h4>
                                    <div className="metric">
                                        <span className="metric-value">${weeklyEarnings.toFixed(2)}</span>
                                        <span className="metric-label">Estimated Weekly Earnings</span>
                                    </div>
                                    <hr className="divider" />
                                    <div className="runway-progress-container">
                                        <div className="runway-header">
                                            <span>Goal Progress</span>
                                            <span>{weeksToGoal} Weeks to takeoff</span>
                                        </div>
                                        <div className="progress-track">
                                            <div className="progress-fill" style={{ width: '45%' }}></div>
                                            <span className="plane-icon" style={{ left: '42%' }}>✈️</span>
                                        </div>
                                    </div>
                                    <div className="runway-stats">
                                        <div className="runway-stat">
                                            <span className="num">{weeksToGoal}</span>
                                            <span className="desc">Weeks of work</span>
                                        </div>
                                        <div className="runway-stat">
                                            <span className="num">{totalChores}</span>
                                            <span className="desc">Chores to complete</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials */}
                <section className="testimonials-section" id="testimonials">
                    <div className="section-header">
                        <h2>Loved by Families Worldwide</h2>
                        <p>Hear how parents are using DadBucks to foster financial wisdom and chore consistency.</p>
                    </div>
                    <div className="testimonials-grid">
                        <div className="testimonial-card" id="test-miller">
                            <div className="stars">⭐⭐⭐⭐⭐</div>
                            <p className="testimonial-text">
                                "DadBucks completely solved the chore war in our house. My kids are actually looking for extra things to do around the house to buy their LEGO sets."
                            </p>
                            <div className="testimonial-author">
                                <span className="author-name">Sarah Jenkins</span>
                                <span className="author-role">Mother of 2 (Ages 7 & 10)</span>
                            </div>
                        </div>
                        <div className="testimonial-card" id="test-jenkins">
                            <div className="stars">⭐⭐⭐⭐⭐</div>
                            <p className="testimonial-text">
                                "As a software developer and dad, I wanted something digital, secure, and intuitive. DadBucks nailed the execution. The offline sync means my kids can use the app anywhere, and the parent PIN prevents any hacks!"
                            </p>
                            <div className="testimonial-author">
                                <span className="author-name">David Miller</span>
                                <span className="author-role">Father of 3 (Ages 6, 9 & 12)</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* App Download SMS Capture */}
                <section id="download" className="download-magnet">
                    <div className="download-container">
                        <h2>Get DadBucks on Your Mobile Device 📱</h2>
                        <p>
                            Track allowances, upload photo proof of chores, and get instant notifications on approvals. Available now for iOS and Android.
                        </p>
                        <form 
                            onSubmit={(e) => {
                                e.preventDefault();
                                alert('Download link sent to your mobile phone! Please check your SMS.');
                                e.target.reset();
                            }} 
                            className="input-row"
                            id="download-sms-form"
                        >
                            <input
                                type="tel"
                                id="download-phone-input"
                                placeholder="Enter your phone number..."
                                required
                            />
                            <button type="submit" className="btn-primary" id="download-submit-btn">Send Link to Phone</button>
                        </form>
                    </div>
                </section>

                {/* Lead Magnet / Email Capture */}
                <section className="email-magnet" id="email-magnet-section">
                    <div className="email-container">
                        <h2>Free Printable Chore Chart Template 🎁</h2>
                        <p>
                            Join our community newsletter and get our ultimate printable, age-appropriate chore chart template sent instantly to your inbox.
                        </p>
                        <form 
                            onSubmit={(e) => {
                                e.preventDefault();
                                alert('Template sent! Please check your email inbox shortly.');
                                e.target.reset();
                            }} 
                            className="input-row"
                            id="email-magnet-form"
                        >
                            <input
                                type="email"
                                id="magnet-email-input"
                                placeholder="Enter your best email..."
                                required
                            />
                            <button type="submit" className="btn-primary" id="magnet-submit-btn">Get Free Template</button>
                        </form>
                    </div>
                </section>

                {/* Feedback / Contact Us */}
                <section className="feedback-section" id="contact-section">
                    <div className="feedback-container">
                        <h3>Have Questions or Feedback? ✉️</h3>
                        <p>We are always looking to improve DadBucks. Shoot us a message directly!</p>
                        <form 
                            className="feedback-form" 
                            id="feedback-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                alert('Thank you for your feedback! Our team has received your message.');
                                e.target.reset();
                            }}
                        >
                            <textarea 
                                name="message" 
                                id="feedback-message"
                                rows="4" 
                                placeholder="How can we help your family?" 
                                required 
                            ></textarea>
                            <button type="submit" className="btn-primary" id="feedback-submit-btn">Submit Feedback</button>
                        </form>
                    </div>
                </section>

                {/* Footer CTA */}
                <section className="cta-footer" id="footer-cta-section">
                    <h2>Ready to transform your household?</h2>
                    <p>Join thousands of parents building the next generation of financially savvy kids.</p>
                    <button className="btn-primary btn-xl" id="footer-cta-btn" onClick={onSignup}>Get Started for Free</button>
                </section>
            </main>

            <footer className="footer" id="footer-section">
                <div className="footer-links">
                    <button onClick={onBlog} className="footer-link" id="footer-link-blog">Blog</button>
                    <span>|</span>
                    <button onClick={onPrivacy} className="footer-link" id="footer-link-privacy">Privacy Policy</button>
                </div>
                <p>&copy; 2026 DadBucks. Built for families, by dads.</p>
            </footer>
        </div>
    );
};

export default LandingPage;
